import { useAppRegion } from '@/hooks/dataHelpers/useCurrentUser';
import { useOverrideNavigator } from '@/hooks/useOverrideNavigator';
import { fetchServerTimeOffset } from '@/lib/commonHelpers';
import { useAuthStore } from '@/store';
import { useCallback, useEffect, useRef, useState } from 'react';

type OnlineCheckResult = {
  success: boolean;
  response?: any;
  error?: any;
};

/**
 * Custom React hook to check online status by pinging a custom URL.
 * Sets status to offline only on timeout and adjusts check interval.
 *
 * @param {number} [interval=60000] - The interval in milliseconds to check the status when online (default is 60 seconds).
 * @param {number} [offlineInterval=5000] - The interval in milliseconds to check the status when offline (default is 5 seconds).
 * @param {number} [timeout=10000] - The timeout in milliseconds for the axios request (default is 10 seconds).
 * @returns {{ isOnline: boolean, checkOnlineStatus: () => Promise<{ success: boolean, error?: any }> }} - An object containing the online status and function to check connectivity.
 */
////////////////////////////////////////////////////////////
// NOTE: Should be used in OnlineStatusContextProvider only once to avoid multiple instances of the hook and multiple intervals
////////////////////////////////////////////////////////////
export const useIsOnlineCore = (interval = 20000, offlineInterval = 5000, timeout = 10000) => {
  const [isOnline, setIsOnline] = useState(true); // Assume online initially
  const [onlineStatus, setOnlineStatus] = useState('online');
  const intervalRef: any = useRef(null); // Use ref to keep track of the interval ID
  const token = useAuthStore((state) => state.token);
  const appRegion = useAppRegion();

  // APL-5761, In case of iPad in latest OS (iOS 17.6 onwards) AAC Mode security, navigator.onLine is always false, so we need to override it to support Wiris math
  useOverrideNavigator({ isOnline });

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setOnlineStatus('online');
    };

    const handleOffline = () => {
      setIsOnline(false);
      setOnlineStatus('offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const checkOnlineStatusFromGoService = useCallback(async (): Promise<OnlineCheckResult> => {
    return fetchServerTimeOffset(appRegion, token, timeout);
  }, [appRegion, token, timeout]);

  // Function that always updates state and returns success/error info
  const checkOnlineStatus = useCallback(async () => {
    if (!token) {
      return { success: false, error: 'No token' };
    }
    // 1️⃣ Primary: AI server
    const goServiceResult = await checkOnlineStatusFromGoService();

    if (goServiceResult.success) {
      setIsOnline(true);
      setOnlineStatus('online');
      return goServiceResult;
    }

    // 3️⃣ Decide offline vs network issue
    const aiError = goServiceResult.error;

    const hasTimeout = (error?: any) =>
      error?.code === 'ECONNABORTED' || error?.message?.toLowerCase().includes('timeout');

    const isNetworkIssue = hasTimeout(aiError);

    console.log('isNetworkIssue', isNetworkIssue);

    setIsOnline(false);
    setOnlineStatus(isNetworkIssue ? 'network_issue' : 'offline');

    return {
      success: false,
      error: {
        aiError,
        status: isNetworkIssue ? 'network_issue' : 'offline',
      },
    };
  }, [token, checkOnlineStatusFromGoService]); // Dependencies for useCallback

  // Function to start the interval
  const startInterval = useCallback(
    (currentIntervalDuration: number) => {
      // Clear any existing interval before setting a new one
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      intervalRef.current = setInterval(checkOnlineStatus, currentIntervalDuration);
    },
    [checkOnlineStatus]
  ); // Dependency for startInterval

  // Effect for managing the interval
  useEffect(() => {
    // Determine the interval duration based on online status
    const currentIntervalDuration = isOnline ? interval : offlineInterval;

    // Start the interval with the determined duration
    startInterval(currentIntervalDuration);

    // Clean up the interval when the component unmounts or dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isOnline, interval, offlineInterval, startInterval]); // Re-run effect if dependencies change

  return {
    isOnline,
    onlineStatus,
    checkOnlineStatus,
  };
};
